import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Trash2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { uploadAPI } from '../services/api';
import toast from 'react-hot-toast';

const EFFECT_TYPES = ["Waterfall", "Mine", "Gerb", "Spark", "Flame", "Comet", "Strobe", "Flash", "Confetti", "Other"];

const EffectModal = ({ effect, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'Waterfall',
    effective_range: 0,
    safety_distance: 0,
    clearance_height: 0,
    duration: 0,
    description: '',
    image_data: null,
    variants: []
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (effect) {
      let variants = [];
      if (effect.variants) {
        try {
          variants = JSON.parse(effect.variants);
        } catch (e) {
          console.error('Error parsing variants:', e);
          variants = [];
        }
      }
      
      setFormData({
        name: effect.name || '',
        type: effect.type || 'Waterfall',
        effective_range: effect.effective_range || 0,
        safety_distance: effect.safety_distance || 0,
        clearance_height: effect.clearance_height || 0,
        duration: effect.duration || 0,
        description: effect.description || '',
        image_data: effect.image_data || null,
        variants: variants
      });
    }
  }, [effect]);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    try {
      setUploading(true);
      const response = await uploadAPI.uploadImage(file);
      setFormData(prev => ({
        ...prev,
        image_data: response.data.image_data
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    multiple: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Effect name is required');
      return;
    }
    
    // Convert variants array to JSON string for storage
    const submissionData = {
      ...formData,
      variants: formData.variants.length > 0 ? JSON.stringify(formData.variants) : null
    };
    
    onSave(submissionData);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { name: '', gas_type: '', range: 0, clearance_height: 0, description: '' }]
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => 
        i === index ? { ...variant, [field]: value } : variant
      )
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>{effect ? 'Edit Effect' : 'Create Effect'}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Effect Name *</label>
            <input
              type="text"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter effect name"
              required
            />
          </div>

          <div className="form-group">
            <label>Type *</label>
            <select
              name="type"
              className="form-control"
              value={formData.type}
              onChange={handleChange}
              required
            >
              {EFFECT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label>Effective Range (m)</label>
              <input
                type="number"
                name="effective_range"
                className="form-control"
                value={formData.effective_range}
                onChange={handleChange}
                min="0"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label>Safety Distance (m)</label>
              <input
                type="number"
                name="safety_distance"
                className="form-control"
                value={formData.safety_distance}
                onChange={handleChange}
                min="0"
                step="0.1"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Clearance Height (m)</label>
            <input
              type="number"
              name="clearance_height"
              className="form-control"
              value={formData.clearance_height}
              onChange={handleChange}
              min="0"
              step="0.1"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter effect description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label>Effect Variants</label>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addVariant}
                style={{ padding: '4px 8px', fontSize: '12px' }}
              >
                <Plus size={14} />
                Add Variant
              </button>
            </div>
            
            {formData.variants.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '14px', fontStyle: 'italic' }}>
                No variants defined. This effect will use the base configuration.
              </p>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '15px' }}>
                {formData.variants.map((variant, index) => (
                  <div key={index} style={{ 
                    marginBottom: index < formData.variants.length - 1 ? '15px' : '0',
                    paddingBottom: index < formData.variants.length - 1 ? '15px' : '0',
                    borderBottom: index < formData.variants.length - 1 ? '1px solid #e5e7eb' : 'none'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                        Variant {index + 1}
                      </h4>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => removeVariant(index)}
                        style={{ padding: '2px 6px', fontSize: '11px' }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    
                    <div className="grid grid-2" style={{ marginBottom: '10px' }}>
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                          Variant Name *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                          placeholder="e.g., Primus 3m, Both 6m"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                          Configuration
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={variant.gas_type}
                          onChange={(e) => updateVariant(index, 'gas_type', e.target.value)}
                          placeholder="e.g., Primus, Propane, LED Pattern, DMX Mode"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-2" style={{ marginBottom: '10px' }}>
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                          Effective Range (m)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={variant.range}
                          onChange={(e) => updateVariant(index, 'range', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.1"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                          Clearance Height (m)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={variant.clearance_height || 0}
                          onChange={(e) => updateVariant(index, 'clearance_height', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.1"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                        Description
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={variant.description}
                        onChange={(e) => updateVariant(index, 'description', e.target.value)}
                        placeholder="Variant description"
                        style={{ fontSize: '13px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Effect Image</label>
            <div
              {...getRootProps()}
              style={{
                border: '2px dashed #ddd',
                borderRadius: '8px',
                padding: '40px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? '#f8f9fa' : 'white'
              }}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <p>Uploading...</p>
              ) : (
                <>
                  <Upload size={32} style={{ margin: '0 auto 10px', color: '#666' }} />
                  {isDragActive ? (
                    <p>Drop the image here...</p>
                  ) : (
                    <p>Drag & drop an effect image here, or click to select</p>
                  )}
                </>
              )}
            </div>
            
            {formData.image_data && (
              <div style={{ marginTop: '10px', textAlign: 'center' }}>
                <img
                  src={`data:image/png;base64,${formData.image_data}`}
                  alt="Effect"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                />
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setFormData(prev => ({ ...prev, image_data: null }))}
                  >
                    Remove Image
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={uploading}>
              {effect ? 'Update Effect' : 'Create Effect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EffectModal;